import Box from "@mui/material/Box"
import Drawer from "@mui/material/Drawer"
import { Container, IconButton, Typography, Button } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"

import {
  loadForecastFilters,
  loadForecastProviders,
  loadingForecastProviders,
  monthDecrement,
  monthIncrement,
  setForecastPrimaryTabs,
  setForecastSecondayTab,
  updateForecastAppendedProvider,
  updateForecastFilters,
  syncForecastToPNL,
} from "../../../redux/slices/forecastSlice"
import { DateSlider, Filters } from "../../../pages/ResourceBudget/Atom"
import LinearLoader from "../../Loader/LinearLoader"
import {
  forecastType,
  resourceBudgetType,
} from "../../../pages/ResourceBudget/types"
import React from "react"
import EditableTable from "../../Table/ResourceBudgetTable/EditableTable"
import {
  getRoles,
  genericForecastGet,
  getProviders,
  saveAllForecastChanges,
} from "../../Table/ResourceBudgetTable/apiUtils"
import { getTokenLocal } from "../../../../helper/utils"
import { primaryTabs as pT } from "../../../pages/ResourceBudget/constants"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { borderStyles, containedButtonStyles } from "../../../../styles/commonStyles"
import ResourceBudgetTabs from "../../Tabs/ResourceBudgetTabs/Index"
import { AlertModal, ConfirmModal } from "../../Tabs/ResourceBudgetTabs/Atom"
import SingleSelectFilter from "../../Dropdown/Filters/Index"
import { setMaevaSelectedPracticesNoAll } from "../../../redux/slices/metricSlice"
import PermsWrapper from "../../Auth/PermsWrapper"
import { permissions } from "../../../../config/constants"
import { setLoading } from "../../../redux/slices/appSlice"
import { toast } from "react-toastify"

interface sidebarProps {
  open: boolean
  toggleOpen: () => void
}

export default function BudgetForcastSidebar({
  open,
  toggleOpen,
}: sidebarProps) {
  const dispatch = useAppDispatch()
  const token = getTokenLocal()
  const resourceBudget: resourceBudgetType = useAppSelector(
    (state) => state.resourceBudget,
  )
  const filtersState: any = useAppSelector((store) => store.metric)

  const { published } = useAppSelector(
    (state: { resourceBudget: any }) => state.resourceBudget,
  )
  const state: forecastType = useAppSelector(
    (state: { forecast: any }) => state.forecast,
  )
  const { primaryIndex, secondaryIndex, primaryTabs, secondayTabs } = state
  const { dataIndex, filters, isLoading, confirmState, editState } = state
  const defaultEditMode = dataIndex === pT.nonClinical
  const appLoading = useAppSelector((state) => state.app.loading)

  // Check if there are any unsaved changes
  const hasUnsavedChanges =
    editState.allChanges.length > 0 ||
    editState.clinicalDoctors.data.length > 0 ||
    editState.clinicalHygienists.data.length > 0 ||
    editState.nonClinical.data.length > 0

  const appendHandler = React.useCallback((data: any) => {
    dispatch(updateForecastAppendedProvider(data))
  }, [])
  const groupChangedHandler = React.useCallback((_: any, value: any) => {
    dispatch(updateForecastFilters(value?.props?.value))
    // dispatch(setMaevaSelectedPracticesNoAll([value?.props?.value]))
  }, [])

  const setError = React.useCallback((error: string) => {
    toast.error(error)
  }, [])

  const saveHandler = React.useCallback(() => {
    saveAllForecastChanges({
      editState,
      office: filters.Offices.selected,
      year: filters.Year,
      month: filters.month,
      token,
      loading: (loadVal: boolean) => dispatch(setLoading(loadVal)),
      setError,
      onSuccess: () => {
        // Clear all changes after successful save
        dispatch(syncForecastToPNL({}))
      },
    })
  }, [editState, filters, token, dispatch, setError])

  React.useEffect(() => {
    dispatch(
      loadForecastFilters({
        Offices: filtersState?.maevaPractices,
        selected: filtersState?.maevaSelectedPracticesNoAll?.[0],
        Year: resourceBudget.selectedYear,
        month: resourceBudget.month,
      }),
    )
  }, [
    filtersState.maevaPractices,
    resourceBudget.selectedYear,
    resourceBudget.month,
  ])

  React.useEffect(() => {
    if (published && open) {
      genericForecastGet[primaryTabs[primaryIndex]]({
        token,
        dispatch,
        ...state,
        office: filters.Offices.selected,
        year: filters.Year,
        month: filters.month,
        isForecast: true,
      })
    }
  }, [dataIndex, filters, published, open])

  React.useEffect(() => {
    if (!defaultEditMode) {
      getProviders({
        dispatch: (data: any) => dispatch(loadForecastProviders(data)),
        loading: (loadVal: boolean) =>
          dispatch(loadingForecastProviders(loadVal)),
        token,
        secondaryIndex,
        office: state.filters.Offices?.selected,
      })
    } else {
      {
        getRoles({
          dispatch: (data: any) => dispatch(loadForecastProviders(data)),
          loading: (loadVal: boolean) => loadingForecastProviders(loadVal),
          token,
        })
      }
    }
  }, [dataIndex, filters])

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      onClose={toggleOpen}
    >
      <Container
        disableGutters
        sx={{
          paddingTop: "18px",
          marginTop: "70px",
          flexDirection: "column",
          display: "flex",
          flexGrow: 1,
          width: { md: "1100px", xs: "auto" },
        }}
      >
        {isLoading && <LinearLoader />}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "30px",
            py: "15px",
            borderBottom: borderStyles.borderBottom,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
              Budget Forecast
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* Global Save Button */}
            {hasUnsavedChanges && (
              <PermsWrapper perm={permissions.AFTER_PUBLISH_OPERATION}>
                <Button
                  disabled={appLoading}
                  variant="contained"
                  sx={{
                    ...containedButtonStyles,
                    minHeight: "40px",
                    minWidth: "80px",
                    px: 1,
                    fontSize: "14px",
                    backgroundColor: "#28a745",
                    "&:hover": {
                      backgroundColor: "#218838",
                    },
                  }}
                  onClick={saveHandler}
                >
                  Save All
                </Button>
              </PermsWrapper>
            )}
            <IconButton onClick={toggleOpen}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <ResourceBudgetTabs
          dirty={editState.dirty?.length > 0 || editState.newEntry}
          key={"forecast-primary-tabs"}
          tabValues={primaryTabs}
          index={primaryIndex}
          dispatchFun={setForecastPrimaryTabs}
          tabContainerStyles={{
            alignItems: "flex-end",
          }}
          optionsContainerStyles={{
            marginBottom: "8px",
            alignItems: "flex-end",
          }}
          additionalOptions={[
            <SingleSelectFilter
              disabled={false}
              changeHandler={groupChangedHandler}
              placeHolder={"Select an office"}
              selectedValue={filters.Offices.selected}
              filterData={filters.Offices.data}
              filterLabel={"Offices"}
              containerStyles={{ mb: -0.7 }}
            />,
            <DateSlider
              dirty={editState.dirty?.length > 0 || editState.newEntry}
              key={"forecast-slider"}
              month={filters.month}
              year={filters.Year}
              monthIncrement={monthIncrement}
              monthDecrement={monthDecrement}
            />,
          ]}
        />
        <ResourceBudgetTabs
          dirty={editState.dirty?.length > 0 || editState.newEntry}
          key={"forecast-seconday-tabs"}
          tabValues={secondayTabs[primaryIndex]}
          dispatchFun={setForecastSecondayTab}
          index={secondaryIndex}
          tabContainerStyles={{ marginTop: "0px", borderBottom: 0 }}
        />
        <Box
          sx={{
            display: "flex",
            position: "relative",
            flexDirection: "column",
            flexGrow: 1,
            maxHeight: "80vh",
          }}
        >
          {open && (
            <EditableTable
              state={state}
              key={"forecast-table"}
              loading={isLoading}
              appendHandler={appendHandler}
              isSlider={true}
            />
          )}
        </Box>
        <ConfirmModal
          close={confirmState.close}
          open={confirmState.open}
          confirmActionHandler={confirmState.actionHandler}
          title={`Do you want to delete the ${confirmState.providerType} "${confirmState.provider}"`}
          optionalMessage=""
          confirmButtonText={"Delete"}
          cancelButtonText={"Cancel"}
          api={{
            loading: confirmState.loading,
          }}
        />
        <AlertModal close={undefined} open={false} title={""} buttonText={""} />
      </Container>
    </Drawer>
  )
}
